import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { MessageCircle } from "lucide-react";

const DEFAULT_WELCOME_BODY =
  "Thanks for opting into the WhatsApp banter group at {pub_name}. " +
  "One tap below joins you straight away — bring the chat.";

// iter175 — default share text shown as placeholder in the editor
// AND used at share-time when the pub hasn't written its own copy.
// Uses {user} (sender's name), {pub_name} (this pub's display name)
// and {link} (branded /register URL). Tokens are documented under the
// textarea so admins can copy-paste them.
const DEFAULT_INVITE_TEXT =
  "{user} invited you to play {pub_name} Football Challenge \u26BD " +
  "Just predict 6 match stats over the 6 game league and see if you " +
  "can beat me and win a prize. Sign up at {link}";

/**
 * iter155/iter156 — Per-pub WhatsApp welcome editor.
 *
 * Two editable fields:
 *   • Invite URL — replaces the platform-wide `WHATSAPP_GROUP_INVITE_URL`
 *     env fallback on the welcome email for THIS pub's users.
 *   • Welcome message — the body copy of the welcome email. Supports
 *     `{name}` and `{pub_name}` placeholders. Empty = use default.
 *
 * The welcome email also includes a branded "come back to the game"
 * link pointing at `{origin}/{slug}` (i.e. `play.amblemead.biz/<slug>`
 * on production).
 */
export function LocalWhatsAppCard({ pub, scope, onSaved }) {
  const applyScope = scope || ((path) => path);
  const [url, setUrl] = useState(pub.whatsapp_group_invite_url || "");
  const [welcomeBody, setWelcomeBody] = useState(pub.whatsapp_welcome_message || "");
  const [inviteText, setInviteText] = useState(pub.whatsapp_invite_text || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setUrl(pub.whatsapp_group_invite_url || "");
    setWelcomeBody(pub.whatsapp_welcome_message || "");
    setInviteText(pub.whatsapp_invite_text || "");
  }, [
    pub.id,
    pub.whatsapp_group_invite_url,
    pub.whatsapp_welcome_message,
    pub.whatsapp_invite_text,
  ]);

  const save = async () => {
    setSaving(true);
    try {
      const trimmedUrl = (url || "").trim();
      const trimmedBody = (welcomeBody || "").trim();
      const trimmedInvite = (inviteText || "").trim();
      const { data } = await api.put(applyScope(`/clients/me/pubs/${pub.id}`), {
        whatsapp_group_invite_url: trimmedUrl,
        whatsapp_welcome_message: trimmedBody,
        whatsapp_invite_text: trimmedInvite,
      });
      toast.success(`${data.name} · WhatsApp saved`);
      onSaved?.(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="rounded-sm border border-zinc-200 bg-white p-5"
      data-testid={`local-whatsapp-card-${pub.slug}`}
    >
      <div className="flex items-center gap-2 mb-4">
        <MessageCircle className="h-4 w-4 text-emerald-600" />
        <p className="font-heading text-sm font-bold uppercase tracking-[0.15em] text-zinc-950">
          {pub.name} — WhatsApp group
        </p>
      </div>

      <Label
        htmlFor={`local-whatsapp-input-${pub.slug}`}
        className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500"
      >
        Invite URL
      </Label>
      <Input
        id={`local-whatsapp-input-${pub.slug}`}
        type="url"
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        placeholder="https://chat.whatsapp.com/…"
        data-testid={`local-whatsapp-url-${pub.slug}`}
        autoComplete="off"
        spellCheck={false}
        maxLength={500}
        className="mt-2 h-10 rounded-sm border-zinc-300 text-sm font-mono"
      />
      <p className="text-[11px] text-zinc-500 mt-2">
        Sent by email to any user who ticks &quot;Add me to the WhatsApp
        group&quot; when signing up on{" "}
        <span className="font-mono">/{pub.slug}</span>. Leave blank to
        use the platform default.
      </p>

      <div className="mt-5">
        <Label
          htmlFor={`local-whatsapp-body-${pub.slug}`}
          className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500"
        >
          Welcome email body copy
        </Label>
        <Textarea
          id={`local-whatsapp-body-${pub.slug}`}
          value={welcomeBody}
          onChange={(e) => setWelcomeBody(e.target.value)}
          placeholder={DEFAULT_WELCOME_BODY}
          data-testid={`local-whatsapp-body-${pub.slug}`}
          rows={4}
          maxLength={1200}
          className="mt-2 rounded-sm border-zinc-300 text-sm"
        />
        <p className="text-[11px] text-zinc-500 mt-2 leading-relaxed">
          The paragraph shown below the &quot;You&apos;re in&quot; headline
          in the welcome email. Use{" "}
          <span className="font-mono bg-zinc-100 px-1 rounded">{"{name}"}</span>{" "}
          for the recipient&apos;s first name and{" "}
          <span className="font-mono bg-zinc-100 px-1 rounded">
            {"{pub_name}"}
          </span>{" "}
          for your pub&apos;s name. A branded link to{" "}
          <span className="font-mono">play.amblemead.biz/{pub.slug}</span>{" "}
          is added automatically. Leave blank to use the default copy.
        </p>
      </div>

      <div className="mt-5">
        <Label
          htmlFor={`local-whatsapp-invite-${pub.slug}`}
          className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500"
        >
          &quot;Invite a friend&quot; WhatsApp text
        </Label>
        <Textarea
          id={`local-whatsapp-invite-${pub.slug}`}
          value={inviteText}
          onChange={(e) => setInviteText(e.target.value)}
          placeholder={DEFAULT_INVITE_TEXT}
          data-testid={`local-whatsapp-invite-text-${pub.slug}`}
          rows={4}
          maxLength={1200}
          className="mt-2 rounded-sm border-zinc-300 text-sm"
        />
        <p className="text-[11px] text-zinc-500 mt-2 leading-relaxed">
          The pre-filled WhatsApp text every player sees when they tap
          the &quot;Invite a friend · WhatsApp&quot; button on your
          matches page. Available tokens:{" "}
          <span className="font-mono bg-zinc-100 px-1 rounded">{"{user}"}</span>{" "}
          (the sender&apos;s name),{" "}
          <span className="font-mono bg-zinc-100 px-1 rounded">
            {"{pub_name}"}
          </span>{" "}
          (this pub) and{" "}
          <span className="font-mono bg-zinc-100 px-1 rounded">{"{link}"}</span>{" "}
          (a personalised link back to your <span className="font-mono">/{pub.slug}</span>{" "}
          sign-up page). Leave blank to use the platform default.
        </p>
      </div>

      <div className="mt-4 flex justify-end">
        <Button
          type="button"
          onClick={save}
          disabled={saving}
          data-testid={`local-whatsapp-save-${pub.slug}`}
          className="h-9 rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-xs px-4"
        >
          {saving ? "Saving…" : "Save WhatsApp settings"}
        </Button>
      </div>
    </div>
  );
}

export default LocalWhatsAppCard;
